import React from 'react';
import { WheelSafeNumberInput } from '../common/WheelSafeNumberInput';
import {
    Create,
    SimpleForm,
    TextInput,
    SelectInput,
    BooleanInput,
    required,
    minValue,
    maxValue,
    maxLength,
    useTranslate,
    useNotify,
    useRedirect,
    Toolbar,
    SaveButton,
} from 'react-admin';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Alert,
    IconButton,
} from '@mui/material';
import {
    ArrowBack,
    Info,
    Settings,
} from '@mui/icons-material';
import { ServerErrorBinder } from '../common/ServerErrorBinder';
import { translateServerMessage } from '../../utils/translateServerMessage';

const CreateToolbar = () => {
    return (
        <Toolbar
            sx={{
                backgroundColor: '#FAFAFA',
                borderTop: '1px solid #E0E0E0',
                px: { xs: 2, sm: 3 },
                py: 2,
                mt: { xs: 2, sm: 4 },
                '& .MuiButton-root': {
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 3,
                    py: 1.5,
                },
            }}
        >
            <SaveButton
                variant="contained"
                sx={{
                    backgroundColor: '#00C853',
                    '&:hover': {
                        backgroundColor: '#00A843',
                        boxShadow: '0 4px 12px rgba(0,200,83,0.3)',
                    },
                }}
            />
        </Toolbar>
    );
};

const PageHeader = () => {
    const redirect = useRedirect();
    const translate = useTranslate();

    return (
        <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 1.5, sm: 2 },
            mb: { xs: 2, sm: 4 },
        }}>
            <IconButton
                onClick={() => redirect('/lms-templates')}
                size="large"
                sx={{
                    backgroundColor: '#FFFFFF',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    '&:hover': {
                        backgroundColor: '#F5F5F5',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                    },
                }}
                title={translate('resources.lms-templates.create.backToList')}
            >
                <ArrowBack />
            </IconButton>
            <Box>
                <Typography sx={{ fontWeight: 600, mb: 0.5, fontSize: { xs: '1.25rem', sm: '2.125rem' } }}>
                    {translate('resources.lms-templates.create.title')}
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '1rem' } }}>
                    {translate('resources.lms-templates.create.subtitle')}
                </Typography>
            </Box>
        </Box>
    );
};

// Maps Zod paths returned by the backend (body.*, body.logic.config.*) to the
// flat form field names used on this Create page. Inputs are flattened here
// (e.g. source="powerUtilizationPercent" rather than "logic.config.power...")
// so we strip those intermediate segments.
const fieldLabelKey = (field: string): string => {
    const known = new Set([
        'name', 'description', 'type', 'isActive',
        'powerUtilizationPercent', 'minPowerPerCharger',
        'powerReductionStep', 'roundToNearest',
    ]);
    if (known.has(field)) {
        return `resources.lms-templates.fields.${field}`;
    }
    return field;
};

const mapBackendPathToCreateField = (path: string): string => {
    let f = path.replace(/^body\./, '').replace(/^params\./, '').replace(/^query\./, '');
    f = f.replace(/^logic\.config\./, '');
    return f;
};

export const LMSTemplatesCreate = () => {
    const translate = useTranslate();
    const notify = useNotify();
    const redirect = useRedirect();
    const [serverErrors, setServerErrors] = React.useState<Record<string, string>>({});

    const [defaultValues, setDefaultValues] = React.useState(() => {
        const duplicatedData = sessionStorage.getItem('lms-template-duplicate');
        if (duplicatedData) {
            try {
                const parsed = JSON.parse(duplicatedData);
                sessionStorage.removeItem('lms-template-duplicate');
                return {
                    name: parsed.name,
                    description: parsed.description,
                    type: parsed.type || 'equal',
                    powerUtilizationPercent: parsed.logic?.config?.powerUtilizationPercent || 90,
                    minPowerPerCharger: parsed.logic?.config?.minPowerPerCharger || 6,
                    powerReductionStep: parsed.logic?.config?.powerReductionStep || 1,
                    roundToNearest: parsed.logic?.config?.roundToNearest || 0.5,
                    isActive: parsed.isActive !== undefined ? parsed.isActive : true,
                };
            } catch (e) {
                console.error('Failed to parse duplicated template data:', e);
            }
        }
        return {
            type: 'equal',
            powerUtilizationPercent: 90,
            minPowerPerCharger: 6,
            powerReductionStep: 1,
            roundToNearest: 0.5,
            isActive: true,
        };
    });

    const handleSave = (data: any) => {
        const finalData = {
            ...data,
            isActive: typeof data.isActive === 'number' ? Boolean(data.isActive) : data.isActive,
            logic: {
                algorithm: 'equal_split',
                config: {
                    powerUtilizationPercent: data.powerUtilizationPercent,
                    minPowerPerCharger: data.minPowerPerCharger,
                    powerReductionStep: data.powerReductionStep,
                    roundToNearest: data.roundToNearest,
                },
            },
        };

        delete finalData.powerUtilizationPercent;
        delete finalData.minPowerPerCharger;
        delete finalData.powerReductionStep;
        delete finalData.roundToNearest;

        return finalData;
    };

    return (
        <Create
            title={false}
            component="div"
            redirect="list"
            transform={handleSave}
            mutationMode="pessimistic"
            mutationOptions={{
                onSuccess: () => {
                    setServerErrors({});
                    notify('resources.lms-templates.notifications.created', { type: 'success' });
                    redirect('/lms-templates');
                },
                onError: (error: any) => {
                    const apiError = error?.body || error;
                    const rawErrors = apiError?.errors as Record<string, string[] | string> | undefined;
                    const fieldErrors: Record<string, string> = {};
                    const summaryLines: string[] = [];
                    if (rawErrors) {
                        for (const [path, msgs] of Object.entries(rawErrors)) {
                            const rawMsg = Array.isArray(msgs) ? msgs[0] : String(msgs);
                            const translated = translateServerMessage(rawMsg, translate);
                            const field = mapBackendPathToCreateField(path);
                            fieldErrors[field] = translated;
                            const labelKey = fieldLabelKey(field);
                            const label = translate(labelKey, { _: field });
                            summaryLines.push(`• ${label}: ${translated}`);
                        }
                    }
                    setServerErrors(fieldErrors);

                    const headerKey = apiError?.errorCode
                        ? `errors.${apiError.errorCode}`
                        : 'validation.formHasErrors';
                    const header = translate(headerKey, { _: apiError?.message || 'Error' });

                    if (summaryLines.length > 0) {
                        notify(`${header}\n${summaryLines.join('\n')}`, {
                            type: 'error',
                            multiLine: true,
                            autoHideDuration: 10000,
                        });
                    } else {
                        notify(header, { type: 'error', autoHideDuration: 6000 });
                    }
                },
            }}
            sx={{
                backgroundColor: 'transparent',
                '& .RaCreate-main': {
                    backgroundColor: 'transparent',
                    boxShadow: 'none',
                },
            }}
        >
            <SimpleForm
                toolbar={<CreateToolbar />}
                defaultValues={defaultValues}
                sx={{
                    '& .MuiPaper-root': {
                        backgroundColor: 'transparent',
                        boxShadow: 'none',
                    },
                    '& > .MuiCardContent-root, & > div > .MuiCardContent-root': {
                        px: { xs: 0, sm: 2 },
                        py: { xs: 1, sm: 2 },
                    },
                    padding: { xs: 0, sm: 'inherit' },
                }}
            >
                <ServerErrorBinder errors={serverErrors} />
                <Box sx={{ width: '100%', maxWidth: 800, margin: '0 auto', pb: { xs: 10, sm: 2 }, '& .MuiFormControl-root': { width: '100%' } }}>
                    <PageHeader />

                    <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                        <CardContent sx={{ p: { xs: 1.5, sm: 3 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 2, sm: 3 } }}>
                                <Info sx={{ mr: 2, color: '#00C853' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                    {translate('resources.lms-templates.sections.basic')}
                                </Typography>
                            </Box>

                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <Box sx={{ '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                                    <TextInput
                                        source="name"
                                        validate={[
                                            required(),
                                            maxLength(100, translate("resources.lms-templates.validation.nameTooLong")),
                                        ]}
                                        fullWidth
                                        helperText={translate('resources.lms-templates.helpers.name')}
                                        sx={{ '& .MuiInputBase-root': { height: '56px' } }}
                                    />
                                </Box>

                                <Box sx={{ '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                                    <TextInput
                                        source="description"
                                        multiline
                                        rows={3}
                                        validate={[
                                            maxLength(500, translate("resources.lms-templates.validation.descriptionTooLong")),
                                        ]}
                                        fullWidth
                                        helperText={translate('resources.lms-templates.helpers.description')}
                                    />
                                </Box>

                                <Box sx={{ '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                                    <SelectInput
                                        source="type"
                                        choices={[
                                            { id: 'equal', name: translate('resources.lms-templates.types.equal') },
                                        ]}
                                        validate={[required()]}
                                        fullWidth
                                        disabled
                                        helperText={translate('resources.lms-templates.helpers.type')}
                                        sx={{ '& .MuiInputBase-root': { height: '56px' } }}
                                    />
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>

                    <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                        <CardContent sx={{ p: { xs: 1.5, sm: 3 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 2, sm: 3 } }}>
                                <Settings sx={{ mr: 2, color: '#00C853' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                    {translate('resources.lms-templates.sections.configuration')}
                                </Typography>
                            </Box>

                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <Alert severity="info" sx={{ mb: 1 }}>
                                    {translate('resources.lms-templates.equalDistribution.description')}
                                </Alert>

                                <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'stretch', sm: 'flex-start' } }}>
                                    <Box sx={{ flex: 1, minWidth: 0, '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                                        <WheelSafeNumberInput
                                            source="powerUtilizationPercent"
                                            defaultValue={90}
                                            validate={[
                                                required(),
                                                minValue(50, translate("resources.lms-templates.validation.utilizationTooLow")),
                                                maxValue(100, translate("resources.lms-templates.validation.utilizationTooHigh")),
                                            ]}
                                            fullWidth
                                            helperText={translate('resources.lms-templates.helpers.powerUtilizationPercent')}
                                            sx={{ '& .MuiInputBase-root': { height: '56px' } }}
                                        />
                                    </Box>
                                    <Box sx={{ flex: 1, minWidth: 0, '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                                        <WheelSafeNumberInput
                                            source="minPowerPerCharger"
                                            defaultValue={6}
                                            validate={[
                                                required(),
                                                minValue(3, translate("resources.lms-templates.validation.minPowerTooLow")),
                                                maxValue(22, translate("resources.lms-templates.validation.minPowerTooHigh")),
                                            ]}
                                            fullWidth
                                            helperText={translate('resources.lms-templates.helpers.minPowerPerCharger')}
                                            sx={{ '& .MuiInputBase-root': { height: '56px' } }}
                                        />
                                    </Box>
                                </Box>

                                <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'stretch', sm: 'flex-start' } }}>
                                    <Box sx={{ flex: 1, minWidth: 0, '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                                        <WheelSafeNumberInput
                                            source="powerReductionStep"
                                            defaultValue={1}
                                            validate={[
                                                required(),
                                                minValue(0.1, translate("resources.lms-templates.validation.stepTooLow")),
                                                maxValue(5, translate("resources.lms-templates.validation.stepTooHigh")),
                                            ]}
                                            fullWidth
                                            helperText={translate('resources.lms-templates.helpers.powerReductionStep')}
                                            sx={{ '& .MuiInputBase-root': { height: '56px' } }}
                                        />
                                    </Box>
                                    <Box sx={{ flex: 1, minWidth: 0, '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                                        <SelectInput
                                            source="roundToNearest"
                                            defaultValue={0.5}
                                            choices={[
                                                { id: 0.1, name: '0.1 kW' },
                                                { id: 0.5, name: '0.5 kW' },
                                                { id: 1, name: '1 kW' },
                                            ]}
                                            validate={[required()]}
                                            fullWidth
                                            helperText={translate('resources.lms-templates.helpers.roundToNearest')}
                                            sx={{ '& .MuiInputBase-root': { height: '56px' } }}
                                        />
                                    </Box>
                                </Box>

                                <BooleanInput
                                    source="isActive"
                                    defaultValue={true}
                                    sx={{ mb: 2 }}
                                />
                            </Box>
                        </CardContent>
                    </Card>
                </Box>
            </SimpleForm>
        </Create>
    );
};
